// TaskOverviewAction.tsx
//
// One-shot "do something once" task drawer (ACTION_CAMPAIGN). Covers
// verification modes TRUSTED and SERVER_CHECK (e.g. join a Telegram bot,
// visit a URL). Unlike the check-in drawer there is no per-day code / polling:
// the flow is simply start -> open the action target -> claim (server verifies).
//
// Visuals intentionally reuse the check-in drawer's stylesheet (`.toac`).

import { useSlice } from "@bridge-applications/provider-kit";
import "./TaskOverviewActionCampaign.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowUpRightFromSquare,
    faPlus,
    faXmark,
} from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import WldIcon from "../../../../../assets/icons/wld-icon.svg?react";
import GoldIcon from "../../../../../assets/icons/gold-icon.svg?react";
import UsdcIcon from "../../../../../assets/icons/usdc-icon.svg?react";
import OroLogoImg from "../../../../../assets/images/coins/oro-icon.webp";
import BgStar from "../../../../../assets/icons/bg-star.svg?react";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import Button3D from "../../../Button3D/Button3D";
import VoucherFaucetABI from "../../../../../abi/MultiTokenVoucherFaucet.json";
import { claim } from "../../../../../claim";
import {
    ApiActionStartReqSchema,
    ApiActionStartResSchema,
    ApiActionCompleteResSchema,
} from "@bridge-applications/eh-types";
import CircularLoader from "../../../CircularLoadingIndicator/CircularLoadingIndicator";
import { API_BASE } from "../../../../../state/config";
import { ExtendedFeedItemSchema } from "../../../../../schemas/slices/FeedSliceSchema";

type LanguageCode = "en" | "es" | "id" | "pt-br" | "th";

interface TaskOverviewActionProps {
    taskId: string;
    onClose: () => void;
}

const API_ACTION_START = `${API_BASE}/api/v1/action/start`;
const API_ACTION_COMPLETE = `${API_BASE}/api/v1/action/complete`;
const API_TG_LINK_INTENT = `${API_BASE}/api/v1/tg/link-intent`;

const VOUCHER_FAUCET_ADDRESS =
    (import.meta as any)?.env?.VITE_VOUCHER_FAUCET_ADDRESS ??
    "0x37b553e5cfb827FC486c70D8dcD6c17Caab76620";

type AnyReward = {
    isPrimaryReward: boolean;
    type: string;
    reward: { unit: string; amount: number };
};

const withRetries = async <T,>(
    fn: () => Promise<T>,
    attempts = 3,
    baseDelayMs = 900,
): Promise<T> => {
    let lastErr: unknown;
    for (let i = 0; i < attempts; i++) {
        try {
            return await fn();
        } catch (e) {
            lastErr = e;
            if (i < attempts - 1) {
                await new Promise((r) =>
                    setTimeout(r, baseDelayMs * Math.pow(2, i)),
                );
            }
        }
    }
    throw lastErr;
};

export default function TaskOverviewAction({
    taskId,
    onClose,
}: TaskOverviewActionProps) {
    type ExtendedFeedItem = z.infer<typeof ExtendedFeedItemSchema>;
    type ActionItem = Extract<
        ExtendedFeedItem,
        { productType: "PARTNER_CAMPAIGN_ACTION" }
    >;

    const { i18n, t } = useTranslation();
    const lang = i18n.language as LanguageCode;
    const { state: feedState, setSlice: setFeedSlice } = useSlice("feedSlice");

    const [isSubmitting, setIsSubmitting] = useState(false);
    // The last claim attempt came back ACTION_NOT_VERIFIED. We surface a
    // tappable prompt that re-opens the target so the user can finish the step.
    const [notVerified, setNotVerified] = useState(false);
    // We attempted to navigate the user to the action target this session.
    const [attemptedOpen, setAttemptedOpen] = useState(false);
    // The user came back to the app after we sent them to the target. The
    // claim affordance is gated on this so we never let someone claim without
    // having actually left for (and returned from) the task.
    const [returned, setReturned] = useState(false);
    const [statusMsg, setStatusMsg] = useState<string | null>(null);
    const [lastTargetUrl, setLastTargetUrl] = useState<string | null>(null);
    // Resolved target URL, fetched ahead of time so the open can happen
    // synchronously inside the click handler (preserves user activation).
    const [prefetchedUrl, setPrefetchedUrl] = useState<string | null>(null);
    const tokenRef = useRef<string | null>(null);
    // True only when /action/start was issued during THIS mount, so we can
    // distinguish "just started, waiting for the user to go do it" from
    // "already started in a previous session" (e.g. after a reload).
    const startedThisSessionRef = useRef(false);

    const task = feedState.items.find(
        (t): t is ActionItem =>
            t.raw.id === taskId && t.productType === "PARTNER_CAMPAIGN_ACTION",
    );

    const cfg = (task?.raw?.action_campaign ?? null) as {
        kind?: string;
        target_url?: string | null;
        verification?: string;
        repeatable?: boolean;
    } | null;
    const kind = cfg?.kind;
    const repeatable = Boolean(cfg?.repeatable);

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const getAccessToken = async (): Promise<string> => {
        if (tokenRef.current) return tokenRef.current;
        const t = await WorldAuthClient.getAccessTokenSilently();
        tokenRef.current = t.accessToken;
        return tokenRef.current;
    };

    const rel = task?.relation;
    const relStatus = rel?.status;
    const hasCompleted = !!rel?.completedAt || relStatus === "COMPLETED";
    // Server-authoritative "started" (participation exists / is in progress).
    const startedServer = !!rel?.startedAt || relStatus === "IN_PROGRESS";
    // Either the server says started, or we've kicked off navigation here.
    const hasStarted = startedServer || attemptedOpen;

    // If the server already shows progress from a previous session (e.g. the
    // user opened the deep link, the app reloaded, and the drawer re-opened),
    // treat them as "returned" so they can claim straight away.
    useEffect(() => {
        if (!startedThisSessionRef.current && startedServer && !hasCompleted) {
            setReturned(true);
        }
    }, [startedServer, hasCompleted]);

    // Detect the user coming back to the app after we sent them to the target.
    useEffect(() => {
        if (!attemptedOpen) return;
        const markReturned = () => setReturned(true);
        const onVisibility = () => {
            if (document.visibilityState === "visible") markReturned();
        };
        window.addEventListener("focus", markReturned);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pageshow", markReturned);
        return () => {
            window.removeEventListener("focus", markReturned);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pageshow", markReturned);
        };
    }, [attemptedOpen]);

    // Resolve the primary reward AND the first secondary ("next") reward so
    // multi-reward campaigns (e.g. WLD + GOLD) render both cards, mirroring the
    // check-in drawer. The server issues every reward on claim regardless; this
    // is purely the display layer.
    const { primary: primaryReward, next: nextReward } = useMemo<{
        primary: AnyReward | null;
        next: AnyReward | null;
    }>(() => {
        if (!task) return { primary: null, next: null };
        let rewards: AnyReward[] = [];
        const raw = task.raw?.rewards as unknown;
        if (Array.isArray(raw)) {
            rewards = raw as AnyReward[];
        } else if ((raw as any)?.items && Array.isArray((raw as any).items)) {
            rewards = (raw as any).items as AnyReward[];
        }

        // Primary = the flagged reward, else the first entry.
        let primaryIdx = rewards.findIndex((r) => r.isPrimaryReward);
        if (primaryIdx < 0 && rewards.length) primaryIdx = 0;
        let primary: AnyReward | null =
            primaryIdx >= 0 ? (rewards[primaryIdx] ?? null) : null;

        // Legacy fallback when no rewards array is present.
        if (
            !primary &&
            task.raw?.primary_reward_amount != null &&
            task.raw?.primary_reward_unit
        ) {
            primary = {
                isPrimaryReward: true,
                type: "CRYPTO",
                reward: {
                    unit: String(task.raw.primary_reward_unit),
                    amount: Number(task.raw.primary_reward_amount),
                },
            };
        }

        // Next = first reward that isn't the primary entry (by index), so we
        // never render the same reward twice.
        const next: AnyReward | null =
            rewards.find((_, i) => i !== primaryIdx) ?? null;

        return { primary, next };
    }, [task]);

    const pickLang = (v: unknown): string | undefined => {
        if (typeof v === "string") return v;
        if (v && typeof v === "object") {
            const obj = v as Record<string, string>;
            return obj[lang] ?? obj.en;
        }
        return undefined;
    };

    const localizedTitle = useMemo(
        () => pickLang(task?.raw?.title) ?? pickLang(task?.title) ?? "",
        [lang, task],
    );

    const localizedDesc = useMemo(
        () => pickLang(task?.raw?.desc) ?? "",
        [lang, task],
    );

    const formatAmount = (n: number) => {
        if (!Number.isFinite(n)) return String(n);
        if (Math.abs(n) < 1) {
            const s = n.toFixed(2);
            return s.replace(/^-?0(?=\.)/, n < 0 ? "-" : "");
        }
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(n);
    };

    const formatReward = (r?: AnyReward | null) =>
        r ? `${formatAmount(r.reward.amount)} ${r.reward?.unit ?? ""}` : "—";

    const rewardUnit = (r?: AnyReward | null) =>
        r?.reward?.unit?.toUpperCase?.() || "";

    const unitClass = (r?: AnyReward | null) => {
        const u = rewardUnit(r);
        if (u === "WLD") return "toac__content__rewards__card--wld";
        if (u === "GOLD") return "toac__content__rewards__card--gold";
        if (u === "USDC") return "toac__content__rewards__card--usdc";
        if (u === "ORO") return "toac__content__rewards__card--oro";
        return "";
    };

    const patchRelation = (patch: Record<string, unknown>) => {
        setFeedSlice({
            ...feedState,
            items: feedState.items.map((it) => {
                if (
                    it.raw.id !== task?.raw.id ||
                    it.productType !== "PARTNER_CAMPAIGN_ACTION"
                )
                    return it;
                const prev = (it.relation ?? {
                    status: "NOT_STARTED",
                    canStart: true,
                    canResume: false,
                }) as NonNullable<ActionItem["relation"]>;
                return {
                    ...it,
                    relation: { ...prev, ...patch },
                } as ActionItem;
            }),
        });
    };

    // Resolve the URL to send the user to. For Telegram we mint a per-user
    // deep link so the bot's /start creates (or refreshes) a subscription the
    // server can verify; for other kinds we use the configured target_url.
    const resolveTargetUrl = async (): Promise<string | undefined> => {
        if (kind === "TELEGRAM_BOT_START") {
            try {
                const token = await getAccessToken();
                const res = await fetch(API_TG_LINK_INTENT, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                const json = await res.json().catch(() => ({}));
                const deepLink: string | undefined = json?.deepLink;
                if (deepLink) return deepLink;
            } catch (e) {
                console.error("link-intent failed", e);
            }
        }
        return cfg?.target_url ?? undefined;
    };

    // Prefetch the target URL when the drawer opens so the tap can open it
    // synchronously (a Telegram deep link requires a /tg/link-intent round
    // trip). Refreshes when the task/kind changes; skipped once completed.
    useEffect(() => {
        if (!task || hasCompleted) return;
        let cancelled = false;
        void (async () => {
            const url = await resolveTargetUrl();
            if (!cancelled && url) setPrefetchedUrl(url);
        })();
        return () => {
            cancelled = true;
        };
    }, [task?.itemId, kind, hasCompleted]);

    // Open a URL the way the rest of the app does: a real new view, falling
    // back to same-view navigation (which always works in the World App
    // webview). No "about:blank" placeholder — that renders a white screen
    // inside the mini-app because the returned handle can't be redirected.
    const openUrl = (url: string) => {
        setLastTargetUrl(url);
        setAttemptedOpen(true);
        const w = window.open(url, "_blank");
        if (!w) {
            window.location.href = url;
        }
    };

    // Re-open the action target for a task that's already started (e.g. after a
    // failed verification). Keeps the click gesture by trying a synchronous
    // open first, then falls back to a resolved URL. For Telegram we mint a
    // fresh single-use deep link for the next tap.
    const reopenTarget = async () => {
        setNotVerified(false);
        const url = prefetchedUrl ?? cfg?.target_url ?? null;
        const win = url ? window.open(url, "_blank") : null;
        if (win && url) {
            setAttemptedOpen(true);
            setLastTargetUrl(url);
        } else {
            const target = url ?? (await resolveTargetUrl());
            if (target) openUrl(target);
        }
        if (kind === "TELEGRAM_BOT_START") {
            void (async () => {
                const fresh = await resolveTargetUrl();
                if (fresh) setPrefetchedUrl(fresh);
            })();
        }
    };

    // Record the task start (creates the participation). Does NOT navigate —
    // opening is handled separately so it can stay inside the click gesture.
    const startAction = async () => {
        if (!task) return;
        setIsSubmitting(true);
        setStatusMsg(null);
        setNotVerified(false);
        try {
            const token = await getAccessToken();
            const body: z.infer<typeof ApiActionStartReqSchema> = {
                partnerCampaignId: task.itemId,
                taskToken: task.listingToken ?? "",
                version: (task as any)?.response_version ?? "1",
                clientVersion: "web",
                timezone: tz,
                renderedSpotsLeft:
                    typeof task.spotsLeft === "number" ? task.spotsLeft : -1,
            };
            const res = await fetch(API_ACTION_START, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            const parsed = ApiActionStartResSchema.safeParse(json);
            if (!res.ok || !parsed.success || parsed.data.status !== "OK") {
                throw new Error(
                    json?.error ??
                        t("TaskOverviewActionCampaign.failedToStartHttp", {
                            status: res.status,
                        }),
                );
            }
            startedThisSessionRef.current = true;
            patchRelation({
                status: "IN_PROGRESS",
                startedAt: new Date().toISOString(),
                canResume: true,
            });
        } catch (e) {
            console.error("Start action failed:", e);
            setStatusMsg(
                e instanceof Error
                    ? e.message
                    : t("TaskOverviewActionCampaign.couldNotStartTask"),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const claimReward = async () => {
        if (!task) return;
        setIsSubmitting(true);
        setStatusMsg(null);
        setNotVerified(false);
        try {
            const token = await getAccessToken();
            const res = await fetch(API_ACTION_COMPLETE, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ taskId: task.itemId }),
            });
            const json = await res.json().catch(() => ({}));
            const parsed = ApiActionCompleteResSchema.safeParse(json);
            if (!res.ok || !parsed.success || parsed.data.status !== "OK") {
                // Most commonly: server could not verify the action yet.
                const msg =
                    json?.error ??
                    (parsed.success
                        ? undefined
                        : t(
                              "TaskOverviewActionCampaign.invalidServerResponse",
                          )) ??
                    t("TaskOverviewActionCampaign.couldNotVerifyYetHttp", {
                        status: res.status,
                    });
                const isNotVerified = String(msg) === "ACTION_NOT_VERIFIED";
                setNotVerified(isNotVerified);
                setStatusMsg(
                    isNotVerified
                        ? t("TaskOverviewActionCampaign.couldNotConfirmYet")
                        : String(msg),
                );
                return;
            }

            const cryptoRewards = parsed.data.rewards.filter(
                (r) => r.data.type === "CRYPTO",
            );
            if (cryptoRewards.length > 0) {
                const txs = cryptoRewards.map((reward) => {
                    const d =
                        reward.data.type === "CRYPTO" ? reward.data.data : null;
                    if (!d) throw new Error("Unexpected reward payload");
                    return {
                        address: VOUCHER_FAUCET_ADDRESS,
                        abi: VoucherFaucetABI.abi,
                        functionName: "claim",
                        args: [d.unitContract, d.amount, d.nonce, d.signature],
                    } as const;
                });

                let batchSucceeded = false;
                try {
                    const batchRes = await withRetries(
                        () => claim(txs as any),
                        2,
                        900,
                    );
                    if ((batchRes as any)?.status !== "error") {
                        batchSucceeded = true;
                    }
                } catch {
                    // fall through to per-voucher retries
                }
                if (!batchSucceeded) {
                    for (const tx of txs) {
                        await withRetries(
                            async () => {
                                const r = await claim([tx] as any);
                                if ((r as any)?.status === "error") {
                                    throw new Error("voucher-claim-failed");
                                }
                                return r;
                            },
                            3,
                            1000,
                        );
                    }
                }
            }

            patchRelation({
                status: "COMPLETED",
                completedAt: new Date().toISOString(),
                canStart: false,
                canResume: false,
            });
        } catch (e) {
            console.error("Claim failed:", e);
            setStatusMsg(
                e instanceof Error
                    ? e.message
                    : t("TaskOverviewActionCampaign.couldNotClaimReward"),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const isOpenStatus =
        task?.status === "AVAILABLE" || task?.status === "LIVE";
    const noSpots =
        typeof task?.spotsLeft === "number" ? task.spotsLeft <= 0 : false;
    const isAvailable =
        isOpenStatus &&
        (!noSpots || hasStarted) &&
        (!hasCompleted || repeatable);

    const openLabel =
        kind === "TELEGRAM_BOT_START"
            ? t("TaskOverviewActionCampaign.openTelegram")
            : t("TaskOverviewActionCampaign.start");
    // Claim is only offered once the user has actually returned from the task.
    const readyToClaim = !hasCompleted && isAvailable && returned;

    let btnLabel = openLabel;
    let btnDisabled = false;
    let stateClass = "state-start";

    if (hasCompleted && !repeatable) {
        btnLabel = t("TaskOverviewActionCampaign.rewardsClaimed");
        btnDisabled = true;
        stateClass = "state-claimed";
    } else if (!isAvailable) {
        btnLabel = t("TaskOverviewActionCampaign.taskUnavailable");
        btnDisabled = true;
        stateClass = "state-unavailable";
    } else if (readyToClaim) {
        btnLabel = t("TaskOverviewActionCampaign.claimReward");
        stateClass = "state-claim";
    } else if (hasStarted) {
        // Started this session but not back yet — let them re-open the target.
        btnLabel =
            kind === "TELEGRAM_BOT_START"
                ? t("TaskOverviewActionCampaign.openTelegramAgain")
                : t("TaskOverviewActionCampaign.openAgain");
        stateClass = "state-open";
    } else {
        btnLabel = openLabel;
        stateClass = "state-start";
    }

    btnDisabled = btnDisabled || isSubmitting;

    const handleClick = async () => {
        if (btnDisabled) return;
        if (hasCompleted && !repeatable) return;
        if (readyToClaim) {
            await claimReward();
            return;
        }

        const url = prefetchedUrl ?? cfg?.target_url ?? null;

        // Synchronously open the real URL (keeps the user gesture, so the
        // World App opens it as a proper external view instead of blocking).
        const win = url ? window.open(url, "_blank") : null;
        if (win && url) {
            setAttemptedOpen(true);
            setLastTargetUrl(url);
            // Page stays alive → record the start without blocking the open.
            if (!hasStarted) void startAction();
        } else {
            // No URL yet or popup blocked: ensure the participation exists,
            // then navigate the current view (always works in the webview).
            if (!hasStarted) await startAction();
            const target = url ?? (await resolveTargetUrl());
            if (target) openUrl(target);
        }

        // Telegram deep-link codes are single-use → mint a fresh one for any
        // subsequent "Open Telegram again" tap.
        if (kind === "TELEGRAM_BOT_START") {
            void (async () => {
                const fresh = await resolveTargetUrl();
                if (fresh) setPrefetchedUrl(fresh);
            })();
        }
    };

    if (!task) return null;

    const primaryRewardUnit = rewardUnit(primaryReward);
    const nextRewardUnit = rewardUnit(nextReward);
    // Telegram deep links are single-use codes, so don't surface a stale
    // "open again" anchor for them — the button re-mints a fresh link instead.
    const reopenUrl =
        kind === "TELEGRAM_BOT_START"
            ? null
            : (lastTargetUrl ?? cfg?.target_url);
    const showReopen = hasStarted && !hasCompleted && !!reopenUrl;

    return (
        <div className="task-overview-action-campaign">
            <div className="toac">
                <div className="toac__header">
                    <div className="toac__header__img-container">
                        <img
                            src={task.raw?.partner_logo_url ?? undefined}
                            alt=""
                            className="toac__header__img"
                        />
                    </div>

                    <button
                        className="toac__header__btn-container"
                        onClick={onClose}
                    >
                        <div className="toac__header__btn">
                            <FontAwesomeIcon icon={faXmark} />
                        </div>
                    </button>
                </div>

                <div className="toac__content">
                    <div className="toac__content__title">
                        <h1 className="toac__content__title__title">
                            {localizedTitle}
                        </h1>
                        <div className="toac__content__title__description-wrapper">
                            <p className="toac__content__title__description">
                                {localizedDesc}
                            </p>
                        </div>
                    </div>

                    <div className="toac__content__rewards">
                        <div
                            className={[
                                "toac__content__rewards__card",
                                "toac__content__rewards__card--primary",
                                // A lone reward keeps the same ~50% width it
                                // would have in a two-reward layout (no `single`
                                // full-width modifier).
                                unitClass(primaryReward),
                            ].join(" ")}
                        >
                            {primaryRewardUnit === "WLD" && (
                                <>
                                    <WldIcon className="toac__content__rewards__card__icon" />
                                    <WldIcon className="toac__content__rewards__card__bg-icon" />
                                </>
                            )}
                            {primaryRewardUnit === "USDC" && (
                                <>
                                    <UsdcIcon className="toac__content__rewards__card__icon" />
                                    <UsdcIcon className="toac__content__rewards__card__bg-icon" />
                                </>
                            )}
                            {primaryRewardUnit === "GOLD" && (
                                <>
                                    <GoldIcon className="toac__content__rewards__card__icon" />
                                    <GoldIcon className="toac__content__rewards__card__bg-icon" />
                                    <BgStar className="toac__content__rewards__card__bg-star pr n3" />
                                    <BgStar className="toac__content__rewards__card__bg-star nx n4" />
                                </>
                            )}
                            {primaryRewardUnit === "ORO" && (
                                <>
                                    <img
                                        src={OroLogoImg}
                                        alt=""
                                        className="toac__content__rewards__card__icon toac__content__rewards__card__icon--img"
                                    />
                                    <img
                                        src={OroLogoImg}
                                        alt=""
                                        className="toac__content__rewards__card__bg-icon toac__content__rewards__card__bg-icon--img"
                                    />
                                </>
                            )}
                            <BgStar className="toac__content__rewards__card__bg-star pr n1" />
                            <BgStar className="toac__content__rewards__card__bg-star pr n2" />

                            <div className="toac__content__rewards__card__value">
                                {formatReward(primaryReward)}
                            </div>
                        </div>

                        {nextReward && (
                            <div className="toac__content__rewards__plus">
                                <FontAwesomeIcon icon={faPlus} />
                            </div>
                        )}

                        {nextReward ? (
                            <div
                                className={[
                                    "toac__content__rewards__card",
                                    "toac__content__rewards__card--next",
                                    unitClass(nextReward),
                                ].join(" ")}
                            >
                                {nextRewardUnit === "GOLD" && (
                                    <>
                                        <GoldIcon className="toac__content__rewards__card__icon" />
                                        <GoldIcon className="toac__content__rewards__card__bg-icon" />
                                    </>
                                )}
                                {nextRewardUnit === "USDC" && (
                                    <>
                                        <UsdcIcon className="toac__content__rewards__card__icon" />
                                        <UsdcIcon className="toac__content__rewards__card__bg-icon" />
                                    </>
                                )}
                                {nextRewardUnit === "WLD" && (
                                    <>
                                        <WldIcon className="toac__content__rewards__card__icon" />
                                        <WldIcon className="toac__content__rewards__card__bg-icon" />
                                    </>
                                )}
                                {nextRewardUnit === "ORO" && (
                                    <>
                                        <img
                                            src={OroLogoImg}
                                            alt=""
                                            className="toac__content__rewards__card__icon toac__content__rewards__card__icon--img"
                                        />
                                        <img
                                            src={OroLogoImg}
                                            alt=""
                                            className="toac__content__rewards__card__bg-icon toac__content__rewards__card__bg-icon--img"
                                        />
                                    </>
                                )}
                                <BgStar className="toac__content__rewards__card__bg-star nx n1" />
                                <BgStar className="toac__content__rewards__card__bg-star nx n2" />

                                <div className="toac__content__rewards__card__value">
                                    {formatReward(nextReward)}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className="toac__content__hdiv" />
                    <div className="toac__content__vspacer" />

                    {showReopen && (
                        <div className="toac__content__reopen">
                            <a
                                href={reopenUrl ?? "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="toac__content__reopen__link"
                            >
                                {t("TaskOverviewActionCampaign.openAgain")}
                            </a>
                        </div>
                    )}

                    {statusMsg &&
                        (notVerified ? (
                            // "We couldn't confirm it yet…" — make it tappable
                            // so the user is sent back to finish the step.
                            <div className="toac__content__reopen">
                                <button
                                    type="button"
                                    className="toac__content__reopen__link toac__content__reopen__link--button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        void reopenTarget();
                                    }}
                                >
                                    {statusMsg}
                                </button>
                            </div>
                        ) : (
                            <div className="toac__content__reopen">
                                <span className="toac__content__reopen__link">
                                    {statusMsg}
                                </span>
                            </div>
                        ))}

                    <div
                        className={[
                            "toac__content__btn",
                            stateClass,
                            btnDisabled ? "disabled" : "",
                        ].join(" ")}
                        aria-disabled={btnDisabled}
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <Button3D
                            onClick={() => handleClick()}
                            className={[
                                "toac__content__btn__btn",
                                stateClass,
                                btnDisabled ? "disabled" : "",
                            ].join(" ")}
                            aria-busy={isSubmitting}
                            disabled={btnDisabled as any}
                            aria-disabled={btnDisabled}
                        >
                            <div className="toac__content__btn__btn__contents">
                                <p>{btnLabel}</p>
                                {isSubmitting ? (
                                    <div className="toac__content__btn__btn__contents__loader">
                                        <CircularLoader />
                                    </div>
                                ) : (
                                    !btnDisabled &&
                                    !readyToClaim && (
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                        />
                                    )
                                )}
                            </div>
                        </Button3D>
                    </div>
                </div>
            </div>
        </div>
    );
}
